/**
 * Audio Upload Limits Configuration
 * ==================================
 * Tier-based audio file size and format restrictions.
 *
 * This mirrors the backend configuration in:
 * api/app/components/tune_ops/audio_limits_config.py
 *
 * IMPORTANT: Keep these in sync with the backend!
 * The frontend uses these for immediate validation (UX),
 * but the backend has the authoritative validation (security).
 */

export interface AudioLimits {
  maxFileSizeBytes: number;
  maxFileSizeMb: number;
  allowedFormats: string[];
  allowedMimeTypes: string[];
  displayFormats: string;
  // react-dropzone accept format
  accept: Record<string, string[]>;
}

export const TIER_AUDIO_LIMITS: Record<string, AudioLimits> = {
  free: {
    maxFileSizeBytes: 10 * 1024 * 1024, // 10MB
    maxFileSizeMb: 10,
    allowedFormats: ['mp3'],
    allowedMimeTypes: ['audio/mpeg', 'audio/mp3'],
    displayFormats: 'MP3',
    accept: {
      'audio/mpeg': ['.mp3'],
    },
  },
  starter: {
    maxFileSizeBytes: 25 * 1024 * 1024, // 25MB
    maxFileSizeMb: 25,
    allowedFormats: ['mp3', 'm4a', 'aac', 'ogg'],
    allowedMimeTypes: [
      'audio/mpeg',
      'audio/mp3',
      'audio/mp4',
      'audio/x-m4a',
      'audio/m4a',
      'audio/aac',
      'audio/ogg',
      'audio/vorbis',
    ],
    displayFormats: 'MP3, M4A, AAC, OGG',
    accept: {
      'audio/mpeg': ['.mp3'],
      'audio/mp4': ['.m4a'],
      'audio/x-m4a': ['.m4a'],
      'audio/aac': ['.aac'],
      'audio/ogg': ['.ogg'],
    },
  },
  pro: {
    maxFileSizeBytes: 75 * 1024 * 1024, // 75MB
    maxFileSizeMb: 75,
    allowedFormats: ['mp3', 'm4a', 'aac', 'ogg', 'wav', 'flac', 'aiff'],
    allowedMimeTypes: [
      'audio/mpeg',
      'audio/mp3',
      'audio/mp4',
      'audio/x-m4a',
      'audio/m4a',
      'audio/aac',
      'audio/ogg',
      'audio/vorbis',
      'audio/wav',
      'audio/wave',
      'audio/x-wav',
      'audio/flac',
      'audio/x-flac',
      'audio/aiff',
      'audio/x-aiff',
    ],
    displayFormats: 'MP3, M4A, AAC, OGG, WAV, FLAC, AIFF',
    accept: {
      'audio/mpeg': ['.mp3'],
      'audio/mp4': ['.m4a'],
      'audio/x-m4a': ['.m4a'],
      'audio/aac': ['.aac'],
      'audio/ogg': ['.ogg'],
      'audio/wav': ['.wav'],
      'audio/wave': ['.wav'],
      'audio/flac': ['.flac'],
      'audio/aiff': ['.aiff', '.aif'],
    },
  },
  family: {
    maxFileSizeBytes: 75 * 1024 * 1024, // 75MB (same as Pro)
    maxFileSizeMb: 75,
    allowedFormats: ['mp3', 'm4a', 'aac', 'ogg', 'wav', 'flac', 'aiff'],
    allowedMimeTypes: [
      'audio/mpeg',
      'audio/mp3',
      'audio/mp4',
      'audio/x-m4a',
      'audio/m4a',
      'audio/aac',
      'audio/ogg',
      'audio/vorbis',
      'audio/wav',
      'audio/wave',
      'audio/x-wav',
      'audio/flac',
      'audio/x-flac',
      'audio/aiff',
      'audio/x-aiff',
    ],
    displayFormats: 'MP3, M4A, AAC, OGG, WAV, FLAC, AIFF',
    accept: {
      'audio/mpeg': ['.mp3'],
      'audio/mp4': ['.m4a'],
      'audio/x-m4a': ['.m4a'],
      'audio/aac': ['.aac'],
      'audio/ogg': ['.ogg'],
      'audio/wav': ['.wav'],
      'audio/wave': ['.wav'],
      'audio/flac': ['.flac'],
      'audio/aiff': ['.aiff', '.aif'],
    },
  },
};

export function getAudioLimitsForTier(tier: string): AudioLimits {
  let tierLower = tier.toLowerCase();

  // Normalize complimentary tiers
  if (tierLower.startsWith('complimentary_')) {
    tierLower = tierLower.replace('complimentary_', '');
  }

  // Map lifetime_access to pro
  if (tierLower === 'lifetime_access') {
    tierLower = 'pro';
  }

  // Map family tier
  if (tierLower === 'family') {
    return TIER_AUDIO_LIMITS.family;
  }

  return TIER_AUDIO_LIMITS[tierLower] || TIER_AUDIO_LIMITS.free;
}
